<template>
  <v-card class="my-1" :style="{ '--layout-primary': themePrimary, '--layout-primary-tint': themePrimaryTint }">
    <v-toolbar flat dense>
      <v-toolbar-title>Layout</v-toolbar-title>
    </v-toolbar>
    <v-card-text>

      <!-- segmented picker: doubles as live preview -->
      <div class="picker-row">
        <div
          class="picker-card"
          :class="{ 'picker-card--selected': layout.currentValue === 'slider' }"
          role="button"
          tabindex="0"
          @click="emitInput(layout.key, 'slider')"
          @keyup.enter="emitInput(layout.key, 'slider')"
        >
          <div v-if="layout.currentValue === 'slider'" class="picker-check"><v-icon x-small color="white">{{ mdiCheck }}</v-icon></div>
          <div class="picker-preview">
            <template v-if="layout.currentValue === 'slider'">
              <div class="picker-amount" :style="{ color: themePrimary }">{{ initial.currentValue }}{{ currencySymbol }}</div>
              <div class="mini-track">
                <div class="mini-track__fill" :style="{ width: startTrackPct + '%', background: themePrimary }"></div>
                <div class="mini-track__thumb" :style="{ left: startTrackPct + '%', background: themePrimary }"></div>
              </div>
            </template>
            <template v-else>
              <div class="mini-track mini-track--ghost">
                <div class="mini-track__thumb mini-track__thumb--ghost" style="left: 35%;"></div>
              </div>
            </template>
          </div>
          <div class="picker-label">Schieberegler</div>
          <div class="picker-sublabel">Freie Betragswahl</div>
        </div>

        <div
          class="picker-card"
          :class="{ 'picker-card--selected': layout.currentValue === 'chips' }"
          role="button"
          tabindex="0"
          @click="emitInput(layout.key, 'chips')"
          @keyup.enter="emitInput(layout.key, 'chips')"
        >
          <div v-if="layout.currentValue === 'chips'" class="picker-check"><v-icon x-small color="white">{{ mdiCheck }}</v-icon></div>
          <div class="picker-preview">
            <template v-if="layout.currentValue === 'chips'">
              <div class="picker-amount" :style="{ color: themePrimary }">{{ initial.currentValue }}{{ currencySymbol }}</div>
              <div class="picker-pills">
                <span v-for="a in amounts" :key="a.key" class="pill" :style="{ background: themePrimary }">{{ a.currentValue / 100 }}{{ currencySymbol }}</span>
              </div>
            </template>
            <template v-else>
              <div class="picker-pills">
                <span v-for="n in 3" :key="n" class="pill pill--ghost"></span>
              </div>
            </template>
          </div>
          <div class="picker-label">Betragswähler</div>
          <div class="picker-sublabel">Drei feste Beträge</div>
        </div>
      </div>

      <!-- Spendenbetrag: Min / Start / Maximum -->
      <div class="section-label">
        <span>SPENDENBETRAG</span>
        <v-tooltip bottom max-width="280">
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" x-small>{{ mdiInformationOutline }}</v-icon>
          </template>
          <span>Mindestbetrag, voreingestellter Startbetrag und maximaler Betrag auf diesem Spendengerät.</span>
        </v-tooltip>
      </div>

      <div class="track-wrap">
        <div class="track"></div>
        <div class="track__fill" :style="{ width: startTrackPct + '%' }"></div>

        <!-- Positions here are fixed and illustrative, not drawn to scale from the actual
             values - real amounts range from 1 to several hundred euros, so a value-accurate
             layout would crowd markers together or make them jump around as you type. -->
        <div class="track-dot track-dot--outline" style="left: 0%;"></div>
        <div v-if="layout.currentValue === 'slider'" class="track-dot track-dot--filled" :style="{ left: startTrackPct + '%', background: themePrimary, boxShadow: '0 0 0 2px ' + themePrimary }"></div>
        <div v-if="layout.currentValue === 'slider'" class="track-dot track-dot--dashed" :style="{ left: softMaxTrackPct + '%', borderColor: themePrimary }"></div>
        <div class="track-dot track-dot--outline track-dot--dark" style="left: 100%;"></div>

        <!-- Mindestbetrag: fixed business rule, not editable -->
        <div class="marker marker--below marker--align-start" style="left: 0%;">
          <div class="marker__label">Mindestbetrag</div>
          <div class="marker__value">{{ initial.minValue }}{{ currencySymbol }}</div>
        </div>

        <!-- Startwert: shown above the track, applies in both layout modes -->
        <div class="marker marker--above" :style="{ left: startTrackPct + '%' }">
          <div class="marker__editable">
            <input
              :id="'id-'+initial.key"
              class="marker__input"
              type="number"
              :value="initial.currentValue"
              @input="val => emitInitial(val.target.value)"
              :min="initial.minValue"
              :max="startUpperBound"
              autocomplete="off"
            >
            <span class="marker__suffix">{{ currencySymbol }}</span>
            <reset-icon class="marker__reset" :setting="initial" @clear="$emit('clear', initial.key)" @set-here="$emit('set-here', initial.key)" />
          </div>
          <div class="marker__caption" :style="{ color: themePrimary }">START</div>
        </div>

        <!-- Slider-Maximum: caps the on-screen slider only, independent from the device Maximum -->
        <div v-if="layout.currentValue === 'slider'" class="marker marker--below" :style="{ left: softMaxTrackPct + '%' }">
          <div class="marker__softmax-row">
            <input
              :id="'id-'+softMax.key"
              class="marker__input marker__input--small"
              type="number"
              :value="softMax.currentValue"
              @input="val => emitClamped(softMax.key, val.target.value, softMax.minValue, softMax.maxValue)"
              :min="softMax.minValue"
              :max="softMax.maxValue"
              autocomplete="off"
            >
            <span class="marker__suffix marker__suffix--small">{{ currencySymbol }}</span>
            <v-tooltip bottom max-width="260">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" x-small>{{ mdiInformationOutline }}</v-icon>
              </template>
              <span>Bis zu diesem Betrag lässt sich der Schieberegler auf dem Gerät ziehen. Ändert nichts am Maximalbetrag rechts &mdash; der gilt für jede Eingabeart.</span>
            </v-tooltip>
            <reset-icon class="marker__reset" :setting="softMax" @clear="$emit('clear', softMax.key)" @set-here="$emit('set-here', softMax.key)" />
          </div>
          <div class="marker__label">Slider-Max</div>
        </div>

        <!-- Maximalbetrag -->
        <div class="marker marker--below marker--right" style="left: 100%;">
          <div class="marker__label">Maximalbetrag</div>
          <div class="marker__editable">
            <input
              :id="'id-'+max.key"
              class="marker__input"
              type="number"
              :value="max.currentValue"
              @input="val => emitClamped(max.key, val.target.value, max.minValue, max.maxValue)"
              :min="max.minValue"
              :max="max.maxValue"
              autocomplete="off"
            >
            <span class="marker__suffix">{{ currencySymbol }}</span>
            <reset-icon class="marker__reset" :setting="max" @clear="$emit('clear', max.key)" @set-here="$emit('set-here', max.key)" />
          </div>
        </div>
      </div>

      <!-- Betragswähler-Beträge (chips only) -->
      <template v-if="layout.currentValue === 'chips'">
        <div class="section-label section-label--spaced">BETRAGSWÄHLER-BETRÄGE</div>
        <beitragswaehler-group
          :settings="amounts"
          :currency="currency"
          @input="$emit('input', $event)"
          @clear="key => $emit('clear', key)"
          @set-here="key => $emit('set-here', key)"
        />
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { mdiCheck, mdiInformationOutline } from '@mdi/js'
import ResetIcon from '@/components/device/ResetIcon'
import BeitragswaehlerGroup from '@/components/device/BeitragswaehlerGroup'

export default {
  name: 'LayoutGroup',
  components: { ResetIcon, BeitragswaehlerGroup },
  props: {
    layout: {
      type: Object,
      required: true,
    },
    initial: {
      type: Object,
      required: true,
    },
    max: {
      type: Object,
      required: true,
    },
    softMax: {
      type: Object,
      required: true,
    },
    amounts: {
      type: Array,
      required: true,
    },
    currency: {
      type: String,
      default: 'EUR',
    },
  },
  data () {
    return {
      mdiCheck,
      mdiInformationOutline,
      // Fixed, illustrative positions for the Startwert/Slider-Max markers on the
      // Spendenbetrag track. These are NOT drawn to scale from the actual amounts -
      // real values range from 1 to several hundred euros, so a value-accurate layout
      // would crowd markers together or make them jump around as the user types. The
      // track only needs to explain what Min/Start/Slider-Max/Maximum mean.
      startTrackPct: 30,
      softMaxTrackPct: 65,
    }
  },
  computed: {
    currencySymbol () {
      return this.$options.filters.currencySymbol(this.currency)
    },
    themePrimary () {
      // kbfrontend's own theme color (red in staging, violet in production) - used for
      // selection/UI chrome only, never for the amounts a donor will actually see.
      return this.$vuetify.theme.currentTheme.primary
    },
    themePrimaryTint () {
      return this.hexToRgba(this.themePrimary, 0.08)
    },
    startUpperBound () {
      // The start amount can never exceed the device Maximum, nor - while the
      // on-screen slider is active - the Slider-Max that caps that slider.
      const bounds = [this.max.currentValue]
      if (this.layout.currentValue === 'slider') {
        bounds.push(this.softMax.currentValue)
      }
      return Math.min(...bounds.filter(v => v !== undefined && v !== null && !isNaN(v)))
    },
  },
  methods: {
    hexToRgba (hex, alpha) {
      let h = String(hex || '').replace('#', '')
      if (h.length === 3) {
        h = h.split('').map(c => c + c).join('')
      }
      const r = parseInt(h.substring(0, 2), 16) || 0
      const g = parseInt(h.substring(2, 4), 16) || 0
      const b = parseInt(h.substring(4, 6), 16) || 0
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },
    emitInput (key, value) {
      this.$emit('input', { key, value })
    },
    emitClamped (key, rawValue, min, max) {
      let value = parseInt(rawValue)
      if (isNaN(value)) {
        return
      }
      value = Math.min(value, max)
      value = Math.max(value, min)
      this.emitInput(key, value)
    },
    emitInitial (rawValue) {
      this.emitClamped(this.initial.key, rawValue, this.initial.minValue, this.startUpperBound)
    },
  },
}
</script>

<style scoped>
.picker-row {
  display: flex;
  gap: 16px;
  margin-bottom: 28px;
}
.picker-card {
  flex: 1;
  position: relative;
  border: 2px solid rgba(0, 0, 0, .15);
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
}
.picker-card--selected {
  border-color: var(--layout-primary);
  background: var(--layout-primary-tint);
}
.picker-check {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--layout-primary);
  display: flex;
  align-items: center;
  justify-content: center;
}
.picker-preview {
  height: 92px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.picker-amount {
  font-size: 30px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 10px;
}
.picker-label {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, .87);
  margin-top: 8px;
}
.picker-sublabel {
  font-size: 12px;
  color: rgba(0, 0, 0, .5);
}
.mini-track {
  align-self: stretch;
  height: 6px;
  border-radius: 3px;
  background: rgba(0, 0, 0, .1);
  position: relative;
  margin: 0 9px;
}
.mini-track__fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 6px;
  border-radius: 3px;
}
.mini-track__thumb {
  position: absolute;
  top: 50%;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .3);
  transform: translate(-50%, -50%);
}
.mini-track__thumb--ghost {
  background: rgba(0, 0, 0, .25);
}
.picker-pills {
  display: flex;
  gap: 8px;
}
.pill {
  border-radius: 16px;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
}
.pill--ghost {
  background: rgba(0, 0, 0, .08) !important;
  border-radius: 8px;
  width: 56px;
  height: 34px;
  padding: 0;
}

.section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, .6);
  letter-spacing: .03em;
  margin-bottom: 30px;
}
.section-label--spaced {
  margin-top: 28px;
  margin-bottom: 14px;
}

.track-wrap {
  position: relative;
  height: 160px;
  margin: 0 10px 8px;
}
.track, .track__fill {
  position: absolute;
  top: 80px;
  height: 8px;
  border-radius: 4px;
}
.track {
  left: 0;
  right: 0;
  background: #e4e4e4;
}
.track__fill {
  left: 0;
  background: var(--layout-primary);
  opacity: .25;
}
/* dots live directly on the track line, independent of the text blocks around them */
.track-dot {
  position: absolute;
  top: 80px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  transform: translate(-50%, -50%);
}
.track-dot--outline {
  border: 3px solid rgba(0, 0, 0, .4);
}
.track-dot--dark {
  border-color: rgba(0, 0, 0, .7);
}
.track-dot--filled {
  border: 3px solid #fff;
}
.track-dot--dashed {
  width: 12px;
  height: 12px;
  border: 2px solid;
  opacity: .55;
}

.marker {
  position: absolute;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
}
.marker--above {
  top: 0;
}
.marker--below {
  top: 100px;
}
.marker--align-start {
  transform: translateX(0);
  align-items: flex-start;
}
.marker--right {
  transform: translateX(-100%);
  align-items: flex-end;
}
.marker__label {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, .6);
  white-space: nowrap;
}
.marker__value {
  font-size: 19px;
  font-weight: 700;
  color: rgba(0, 0, 0, .8);
}
.marker__caption {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .04em;
  margin-top: 6px;
}
.marker__editable {
  display: flex;
  align-items: center;
  gap: 2px;
  background: rgba(0, 0, 0, .05);
  border: 1px solid rgba(0, 0, 0, .15);
  border-radius: 8px;
  padding: 3px 4px 3px 10px;
}
.marker__editable:hover {
  border-color: rgba(0, 0, 0, .3);
}
.marker__editable:focus-within {
  border-color: var(--layout-primary);
  background: #fff;
}
.marker__input {
  width: 4.5ch;
  border: none;
  text-align: right;
  font-size: 19px;
  font-weight: 700;
  color: rgba(0, 0, 0, .8);
  font-family: inherit;
  background: transparent;
  -moz-appearance: textfield;
  cursor: text;
}
.marker__input::-webkit-outer-spin-button,
.marker__input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.marker__input:focus {
  outline: none;
}
.marker__input--small {
  font-size: 13px;
  font-weight: 500;
  width: 3.5ch;
  color: rgba(0, 0, 0, .55);
}
.marker__softmax-row {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-top: 6px;
  background: rgba(0, 0, 0, .05);
  border: 1px solid rgba(0, 0, 0, .15);
  border-radius: 8px;
  padding: 3px 4px 3px 10px;
}
.marker__softmax-row:hover {
  border-color: rgba(0, 0, 0, .3);
}
.marker__softmax-row:focus-within {
  border-color: var(--layout-primary);
  background: #fff;
}
.marker__suffix {
  font-size: 15px;
  color: rgba(0, 0, 0, .55);
}
.marker__suffix--small {
  font-size: 13px;
}
.marker__reset {
  margin-top: 4px;
}
</style>
